import { Theme } from '@mui/material/styles'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      alignItems: 'center',
      background: 'rgba(28, 25, 21, .42)',
      color: '#211f1c',
      display: 'flex',
      height: '100dvh',
      inset: 0,
      justifyContent: 'center',
      overscrollBehavior: 'none',
      overflow: 'hidden',
      padding: '1.5dvh .75vw',
      position: 'fixed',
      zIndex: 5000,
      [theme.breakpoints.down('sm')]: {
        padding: 0,
      },
    },
    shell: {
      background: '#fffdf8',
      boxShadow: '0 28px 90px rgba(28, 25, 21, .28)',
      display: 'grid',
      gridTemplateRows: 'auto 1fr',
      height: 'min(96dvh, 1060px)',
      maxWidth: 2140,
      minHeight: 0,
      overflow: 'hidden',
      width: 'min(98.5vw, 2140px)',
      [theme.breakpoints.down('sm')]: {
        height: '100dvh',
        width: '100vw',
      },
    },
    toolbar: {
      alignItems: 'center',
      background: 'rgba(248, 244, 237, .94)',
      boxShadow: '0 1px 0 rgba(33, 31, 28, .06)',
      display: 'flex',
      gap: theme.spacing(1),
      justifyContent: 'space-between',
      padding: theme.spacing(1.25, 2),
      position: 'relative',
      zIndex: 3,
      [theme.breakpoints.down('sm')]: {
        minHeight: 78,
        padding: theme.spacing(1.25, 1.5),
      },
    },
    toolbarTitle: {
      minWidth: 0,
      textAlign: 'center',
      '& strong': {
        display: 'block',
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '1.12rem',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      '& span': {
        color: 'rgba(33, 31, 28, .62)',
        display: 'block',
        fontSize: '.78rem',
      },
      [theme.breakpoints.down('sm')]: {
        textAlign: 'left',
        '& strong': {
          fontSize: '1.35rem',
          maxWidth: '52vw',
        },
        '& span': {
          fontSize: '1rem',
        },
      },
    },
    overview: {
      minHeight: 0,
      overflow: 'auto',
      padding: theme.spacing(2, 2, 6),
    },
    overviewGrid: {
      columnCount: 3,
      columnGap: theme.spacing(3),
      display: 'block',
      gap: theme.spacing(3),
      margin: '0 auto',
      maxWidth: 1380,
      '& $overviewCard': {
        breakInside: 'avoid',
        marginBottom: theme.spacing(3),
      },
      '& $overviewCard:nth-child(5n + 1)': {
        transform: 'translateY(10px)',
      },
      '& $overviewCard:nth-child(5n + 2)': {
        transform: 'translateY(-6px)',
      },
      '& $overviewCard:nth-child(4n + 1) $overviewCover': {
        aspectRatio: '4 / 5',
      },
      '& $overviewCard:nth-child(4n + 2) $overviewCover': {
        aspectRatio: '1 / 1',
      },
      [theme.breakpoints.down('md')]: {
        columnCount: 2,
      },
      [theme.breakpoints.down('sm')]: {
        columnCount: 1,
      },
    },
    overviewCard: {
      background: '#fff',
      border: '1px solid rgba(33, 31, 28, .1)',
      borderRadius: 0,
      boxShadow: '0 18px 50px rgba(33, 31, 28, .12)',
      color: 'inherit',
      cursor: 'pointer',
      display: 'grid',
      gap: theme.spacing(1.5),
      gridTemplateRows: 'auto 1fr',
      minHeight: 460,
      padding: theme.spacing(1.25),
      textAlign: 'left',
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        minHeight: 380,
      },
    },
    overviewCover: {
      aspectRatio: '3 / 4',
      background: '#e7e1d7',
      display: 'grid',
      overflow: 'hidden',
      placeItems: 'center',
      position: 'relative',
      '& img': {
        height: '100%',
        objectFit: 'cover',
        width: '100%',
      },
    },
    overviewInfo: {
      alignContent: 'start',
      display: 'grid',
      gap: theme.spacing(.5),
      '& h2': {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '1.45rem',
        lineHeight: 1,
        margin: 0,
      },
      '& p': {
        color: 'rgba(33, 31, 28, .68)',
        margin: 0,
      },
    },
    reader: {
      background: 'linear-gradient(180deg, #eee9df, #e5ded2)',
      height: '100%',
      minHeight: 0,
      overscrollBehavior: 'none',
      overflow: 'hidden',
      padding: 0,
      position: 'relative',
    },
    readerProductImages: {
      background: 'linear-gradient(180deg, #221f1a, #141210)',
      '& $stage': {
        padding: theme.spacing(2, 2, 6),
      },
      '& $track': {
        width: 'min(100%, 1320px)',
      },
      '& $spread': {
        alignItems: 'start',
      },
      [theme.breakpoints.down('sm')]: {
        '& $stage': {
          padding: theme.spacing(1, .75, 6),
        },
      },
    },
    navButton: {
      background: 'rgba(255, 252, 244, .9)',
      border: 0,
      boxShadow: '0 16px 38px rgba(65, 52, 38, .2)',
      color: '#211f1c',
      height: 58,
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 58,
      zIndex: 4,
      '&:hover': {
        background: '#fffaf1',
      },
      '&.Mui-disabled': {
        background: 'rgba(255, 252, 244, .54)',
        opacity: .22,
      },
      [theme.breakpoints.down('sm')]: {
        bottom: theme.spacing(2),
        height: 48,
        top: 'auto',
        transform: 'none',
        width: 48,
      },
    },
    zoomControls: {
      alignItems: 'center',
      background: 'rgba(255, 253, 248, .96)',
      bottom: theme.spacing(1.5),
      boxShadow: '0 12px 34px rgba(65, 52, 38, .16)',
      display: 'flex',
      gap: theme.spacing(.25),
      left: '50%',
      padding: theme.spacing(.25, .75),
      position: 'absolute',
      transform: 'translateX(-50%)',
      zIndex: 5,
      '& span': {
        color: 'rgba(33, 31, 28, .64)',
        fontSize: '.78rem',
        minWidth: 42,
        textAlign: 'center',
      },
      [theme.breakpoints.down('sm')]: {
        bottom: theme.spacing(1),
        '& .MuiButton-root': {
          minWidth: 54,
          paddingLeft: theme.spacing(.5),
          paddingRight: theme.spacing(.5),
        },
      },
    },
    navButtonPrev: {
      left: theme.spacing(2),
    },
    navButtonNext: {
      right: theme.spacing(2),
    },
    stage: {
      height: '100%',
      minHeight: 0,
      overscrollBehavior: 'contain',
      overflowX: 'hidden',
      overflowY: 'auto',
      padding: theme.spacing(1.5, 1.5, 5.5),
      scrollbarWidth: 'thin',
      touchAction: 'pan-y',
      width: '100%',
      WebkitOverflowScrolling: 'touch',
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(1.5, 1.25, 5.5),
      },
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(1.5, .75, 5.5),
      },
    },
    track: {
      display: 'block',
      height: 'auto',
      margin: '0 auto',
      maxWidth: '100%',
      minHeight: 0,
      width: 'min(100%, var(--magazine-track-width, 1476px))',
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    spread: {
      alignItems: 'stretch',
      boxSizing: 'border-box',
      display: 'grid',
      gap: 'var(--magazine-page-gap, 12px)',
      gridTemplateColumns: 'minmax(0, 1fr)',
      height: 'auto',
      justifyContent: 'center',
      justifyItems: 'stretch',
      minHeight: 0,
      minWidth: 0,
      overscrollBehavior: 'contain',
      overflowX: 'hidden',
      overflowY: 'visible',
      padding: 0,
      [theme.breakpoints.down('md')]: {
        padding: 0,
      },
      [theme.breakpoints.down('sm')]: {
        alignItems: 'start',
        display: 'grid',
        gridTemplateColumns: '1fr',
        height: 'auto',
        padding: 0,
        '&::-webkit-scrollbar': {
          display: 'none',
        },
      },
    },
    singleSpread: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      justifyContent: 'center',
      '& $page': {
        width: '100%',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        '& $page': {
          width: '100%',
        },
      },
    },
    doubleSpread: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    page: {
      aspectRatio: 'auto',
      background: '#fffdf8',
      boxShadow: 'inset 22px 0 38px -34px rgba(75, 61, 45, .5), inset -22px 0 38px -34px rgba(75, 61, 45, .5)',
      boxSizing: 'border-box',
      display: 'grid',
      maxHeight: 'none',
      minHeight: 'var(--magazine-page-height, 620px)',
      minWidth: 0,
      overflow: 'hidden',
      padding: theme.spacing(5.5, 6.5),
      position: 'relative',
      width: '100%',
      '&::before': {
        background: 'linear-gradient(90deg, rgba(69, 53, 34, .12) 0, rgba(69, 53, 34, 0) 34px, rgba(69, 53, 34, 0) calc(100% - 34px), rgba(69, 53, 34, .12) 100%)',
        boxShadow: 'inset 0 0 0 1px rgba(33, 31, 28, .035)',
        content: '""',
        inset: 0,
        pointerEvents: 'none',
        position: 'absolute',
        zIndex: 2,
      },
      '& > *:not($imageGrid):not($pageMark):not($pageTextLine)': {
        position: 'relative',
        zIndex: 3,
      },
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(4.5, 4.5),
      },
      [theme.breakpoints.down('sm')]: {
        display: 'block',
        minHeight: 'var(--magazine-page-height, auto)',
        overflowX: 'hidden',
        padding: theme.spacing(2.5, 2.25, 3.25),
        '& > * + *': {
          marginTop: theme.spacing(1.25),
        },
      },
    },
    textAuto: {},
    textTopLeft: {
      alignContent: 'start',
      justifyItems: 'start',
    },
    textTopRight: {
      alignContent: 'start',
      justifyItems: 'end',
      textAlign: 'right',
    },
    textCenter: {
      alignContent: 'center',
      justifyItems: 'center',
      textAlign: 'center',
    },
    textBottomLeft: {
      alignContent: 'end',
      justifyItems: 'start',
    },
    textBottomRight: {
      alignContent: 'end',
      justifyItems: 'end',
      textAlign: 'right',
    },
    textLeftColumn: {
      gridTemplateColumns: '1fr 1fr',
      '& $pageBody': {
        columns: 1,
        gridColumn: 1,
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    },
    textRightColumn: {
      gridTemplateColumns: '1fr 1fr',
      '& $pageBody': {
        columns: 1,
        gridColumn: 2,
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        '& $pageBody': {
          gridColumn: 1,
        },
      },
    },
    pageKicker: {
      color: 'var(--magazine-accent)',
      fontSize: '.75rem',
      fontWeight: 700,
      letterSpacing: 0,
      textTransform: 'uppercase',
    },
    pageTitle: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: 'clamp(1.75rem, 3.6vw, 3.7rem)',
      hyphens: 'auto',
      lineHeight: .9,
      margin: 0,
      maxWidth: '100%',
      overflowWrap: 'break-word',
      [theme.breakpoints.down('md')]: {
        fontSize: '2.75rem',
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '2rem',
        lineHeight: .98,
      },
    },
    pageSubtitle: {
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: '.98rem',
      fontStyle: 'italic',
      opacity: .78,
    },
    pageBody: {
      columnGap: theme.spacing(4),
      columns: 2,
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: '.95rem',
      hyphens: 'auto',
      lineHeight: 1.5,
      maxWidth: 980,
      overflowWrap: 'break-word',
      '& p': {
        margin: '0 0 .8rem',
      },
      [theme.breakpoints.down('sm')]: {
        columns: 1,
        fontSize: '.94rem',
        lineHeight: 1.52,
      },
    },
    pageMark: {
      maxWidth: 'min(34vw, 360px)',
      pointerEvents: 'none',
      position: 'absolute',
      zIndex: 5,
      '& img': {
        display: 'block',
        height: 'auto',
        maxHeight: 132,
        maxWidth: '100%',
        objectFit: 'contain',
        width: 'min(var(--magazine-logo-width, 180px), 100%)',
      },
      [theme.breakpoints.down('sm')]: {
        maxWidth: '42vw',
        '& img': {
          maxHeight: 86,
        },
      },
    },
    pageTextLine: {
      color: 'currentColor',
      fontFamily: 'Georgia, "Times New Roman", serif',
      fontSize: '.95rem',
      fontStyle: 'italic',
      maxWidth: 'min(42vw, 420px)',
      opacity: .72,
      pointerEvents: 'none',
      position: 'absolute',
      zIndex: 5,
      [theme.breakpoints.down('sm')]: {
        fontSize: '.8rem',
        maxWidth: '62vw',
      },
    },
    overlayTopLeft: {
      left: theme.spacing(4),
      top: theme.spacing(3),
    },
    overlayTopCenter: {
      left: '50%',
      top: theme.spacing(3),
      transform: 'translateX(-50%)',
      textAlign: 'center',
    },
    overlayTopRight: {
      right: theme.spacing(4),
      top: theme.spacing(3),
      textAlign: 'right',
      '& img': {
        marginLeft: 'auto',
      },
    },
    overlayMiddleLeft: {
      left: theme.spacing(4),
      top: '50%',
      transform: 'translateY(-50%)',
    },
    overlayMiddleCenter: {
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      textAlign: 'center',
    },
    overlayMiddleRight: {
      right: theme.spacing(4),
      top: '50%',
      transform: 'translateY(-50%)',
      textAlign: 'right',
      '& img': {
        marginLeft: 'auto',
      },
    },
    overlayBottomLeft: {
      bottom: theme.spacing(3),
      left: theme.spacing(4),
    },
    overlayBottomCenter: {
      bottom: theme.spacing(3),
      left: '50%',
      transform: 'translateX(-50%)',
      textAlign: 'center',
    },
    overlayBottomRight: {
      bottom: theme.spacing(3),
      right: theme.spacing(4),
      textAlign: 'right',
      '& img': {
        marginLeft: 'auto',
      },
    },
    imageGrid: {
      display: 'grid',
      gap: theme.spacing(1),
      minHeight: 0,
      minWidth: 0,
      '& figure': {
        margin: 0,
        minHeight: 0,
        overflow: 'hidden',
        position: 'relative',
        transform: 'translate(var(--magazine-image-overlap-x, 0), var(--magazine-image-overlap-y, 0)) scale(var(--magazine-image-scale, 1))',
      },
      '& img': {
        display: 'block',
        height: '100%',
        objectFit: 'cover',
        width: '100%',
      },
      '& figcaption': {
        background: 'rgba(255, 255, 255, .82)',
        bottom: 0,
        fontSize: '.72rem',
        left: 0,
        padding: theme.spacing(.4, .6),
        position: 'absolute',
        right: 0,
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        margin: theme.spacing(1.5, 0),
        maxHeight: 'none',
        '& figure': {
          minHeight: 180,
        },
        '& img': {
          minHeight: 180,
        },
      },
    },
    captionAuto: {},
    captionInsideTop: {
      '& figcaption': {
        bottom: 'auto',
        top: 0,
      },
    },
    captionOutside: {
      overflow: 'visible !important',
      '& figcaption': {
        background: 'transparent',
        bottom: 'auto',
        color: 'currentColor',
        left: 0,
        padding: theme.spacing(.35, 0, 0),
        position: 'static',
        right: 'auto',
      },
    },
    captionHidden: {
      '& figcaption': {
        display: 'none',
      },
    },
    imageStandard: {
      aspectRatio: '4 / 3',
      minHeight: 160,
      [theme.breakpoints.down('sm')]: {
        minHeight: 180,
      },
    },
    imageWide: {
      aspectRatio: '16 / 7',
      gridColumn: '1 / -1',
      minHeight: 190,
      [theme.breakpoints.down('sm')]: {
        minHeight: 190,
      },
    },
    imageFullscreen: {
      inset: 0,
      position: 'absolute',
      zIndex: 0,
      '& img': {
        height: '100%',
        opacity: .72,
      },
      [theme.breakpoints.down('sm')]: {
        inset: 'auto',
        minHeight: 220,
        position: 'relative',
        '& img': {
          opacity: 1,
        },
      },
    },
    imageHero: {
      aspectRatio: '16 / 8',
      gridColumn: '1 / -1',
      minHeight: 220,
      [theme.breakpoints.down('sm')]: {
        minHeight: 220,
      },
    },
    imagePortrait: {
      aspectRatio: '3 / 4',
      minHeight: 220,
    },
    imageInset: {
      alignSelf: 'end',
      aspectRatio: '4 / 3',
      background: '#fff',
      boxShadow: '0 14px 34px rgba(33, 31, 28, .2)',
      justifySelf: 'end',
      maxWidth: '78%',
      minHeight: 150,
      padding: theme.spacing(.45),
      [theme.breakpoints.down('sm')]: {
        justifySelf: 'stretch',
        maxWidth: '100%',
      },
    },
    imageCircle: {
      aspectRatio: '1 / 1',
      borderRadius: '50%',
      justifySelf: 'center',
      width: 'min(100%, 420px)',
      '&$captionAuto': {
        overflow: 'visible !important',
      },
      '&$captionAuto figcaption': {
        background: 'transparent',
        bottom: 'auto',
        color: 'currentColor',
        left: 0,
        padding: theme.spacing(.35, 0, 0),
        position: 'static',
        right: 'auto',
        textAlign: 'center',
      },
      '& img': {
        borderRadius: '50%',
      },
      [theme.breakpoints.down('sm')]: {
        width: 'min(100%, 300px)',
      },
    },
    imageCutout: {
      clipPath: 'polygon(18% 0, 100% 0, 84% 100%, 0 82%)',
      '&$captionAuto': {
        overflow: 'visible !important',
      },
      '&$captionAuto figcaption': {
        background: 'transparent',
        bottom: 'auto',
        color: 'currentColor',
        left: 0,
        padding: theme.spacing(.35, 0, 0),
        position: 'static',
        right: 'auto',
      },
    },
    imageHotspot: {
      alignItems: 'center',
      background: 'var(--magazine-accent)',
      border: '2px solid #fff',
      borderRadius: '50%',
      boxShadow: '0 12px 24px rgba(0, 0, 0, .2), 0 0 0 5px rgba(255, 255, 255, .36)',
      color: '#fff',
      cursor: 'pointer',
      display: 'inline-flex',
      height: 18,
      justifyContent: 'center',
      minWidth: 18,
      padding: 0,
      position: 'absolute',
      transform: 'translate(-50%, -50%)',
      width: 18,
      zIndex: 8,
      '&::before': {
        border: '1px solid rgba(255, 255, 255, .75)',
        borderRadius: '50%',
        content: '""',
        height: 30,
        left: '50%',
        position: 'absolute',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: 30,
      },
      '&::after': {
        background: '#fff',
        borderRadius: '50%',
        content: '""',
        height: 6,
        width: 6,
      },
      '&[aria-disabled="true"]': {
        cursor: 'default',
        opacity: .78,
      },
      '& span': {
        background: '#fff',
        boxShadow: '0 10px 24px rgba(0, 0, 0, .16)',
        color: '#211f1c',
        fontSize: '.72rem',
        left: '50%',
        maxWidth: 180,
        opacity: 0,
        padding: theme.spacing(.35, .55),
        pointerEvents: 'none',
        position: 'absolute',
        top: 'calc(100% + 8px)',
        transform: 'translateX(-50%)',
        transition: 'opacity .16s ease',
        whiteSpace: 'nowrap',
      },
      '&:hover span, &:focus-visible span': {
        opacity: 1,
      },
      [theme.breakpoints.down('sm')]: {
        boxShadow: '0 8px 18px rgba(0, 0, 0, .18), 0 0 0 3px rgba(255, 255, 255, .32)',
        height: 14,
        minWidth: 14,
        width: 14,
        '&::before': {
          height: 24,
          width: 24,
        },
        '&::after': {
          height: 4,
          width: 4,
        },
      },
    },
    products: {
      display: 'grid',
      gap: theme.spacing(1.25),
      gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
      minWidth: 0,
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    },
    productTile: {
      background: 'rgba(255, 253, 248, .94)',
      border: 0,
      boxShadow: '0 14px 34px rgba(50, 38, 24, .12)',
      color: 'inherit',
      cursor: 'pointer',
      display: 'grid',
      gap: theme.spacing(.6),
      padding: theme.spacing(.9),
      textAlign: 'left',
      minWidth: 0,
      '& img': {
        aspectRatio: '1 / 1',
        background: '#fff',
        objectFit: 'contain',
        width: '100%',
      },
      '& strong': {
        fontSize: '.9rem',
        lineHeight: 1.2,
        overflowWrap: 'break-word',
      },
      '& span': {
        color: 'rgba(33, 31, 28, .62)',
        fontSize: '.78rem',
        overflowWrap: 'break-word',
      },
      [theme.breakpoints.down('sm')]: {
        alignItems: 'start',
        gridTemplateColumns: '88px 1fr',
        minHeight: 110,
        padding: theme.spacing(1),
        '& img': {
          gridRow: '1 / span 4',
        },
        '& strong': {
          fontSize: '1rem',
        },
      },
    },
    productTileImage: {
      display: 'block',
      lineHeight: 0,
      position: 'relative',
    },
    templateCoverGrid: {
      gridTemplateRows: 'auto auto auto auto',
      '& $imageGrid': {
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
      '& $imageWide:first-child': {
        aspectRatio: '16 / 8.5',
        minHeight: 220,
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateRows: 'none',
        '& $imageGrid': {
          gridTemplateColumns: '1fr',
        },
      },
    },
    templateEditorialIndex: {
      gridTemplateRows: 'auto auto auto auto',
      '& $pageBody': {
        columns: 2,
      },
      '& $imageGrid': {
        gridTemplateColumns: '1fr',
        maxHeight: 220,
      },
      [theme.breakpoints.down('sm')]: {
        '& $pageBody': {
          columns: 1,
        },
      },
    },
    templateSplitArticle: {
      gridTemplateColumns: '1.05fr .95fr',
      gridTemplateRows: 'auto auto auto',
      '& $pageBody': {
        columns: 1,
        gridColumn: 1,
      },
      '& $imageGrid': {
        gridColumn: 2,
        gridRow: '1 / span 3',
        gridTemplateColumns: '1fr',
      },
      '& $imageGrid figure': {
        minHeight: '100%',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        '& $imageGrid': {
          gridColumn: 1,
          gridRow: 'auto',
        },
        '& $imageGrid figure': {
          minHeight: 200,
        },
      },
    },
    templateImageEssay: {
      alignContent: 'end',
      background: '#1f5f9c',
      color: '#fff',
      minHeight: 'var(--magazine-page-height, 100%)',
      '&::after': {
        background: 'rgba(31, 83, 122, .5)',
        content: '""',
        inset: 0,
        pointerEvents: 'none',
        position: 'absolute',
        zIndex: 1,
      },
      '& $pageTitle': {
        fontSize: '3.75rem',
        maxWidth: '82%',
      },
      '& $pageBody': {
        columns: 1,
        fontSize: '1rem',
        maxWidth: '72%',
      },
      '& $imageGrid': {
        inset: 0,
        position: 'absolute',
        zIndex: 0,
      },
      '& $imageGrid figure:first-child': {
        gridColumn: '1 / -1',
      },
      '& $imageGrid img': {
        opacity: 1,
      },
      '& > *:not($imageGrid)': {
        position: 'relative',
        zIndex: 3,
      },
      [theme.breakpoints.down('sm')]: {
        alignContent: 'start',
        minHeight: 'var(--magazine-page-height, calc(100dvh - 108px))',
        '& $pageTitle': {
          fontSize: '2.05rem',
          maxWidth: '100%',
        },
        '& $pageBody': {
          fontSize: '.95rem',
          maxWidth: '100%',
        },
        '& $imageGrid': {
          inset: 0,
          margin: 0,
          position: 'absolute',
          zIndex: 0,
        },
        '& $imageFullscreen': {
          inset: 0,
          minHeight: '100%',
          position: 'absolute',
        },
        '& $imageFullscreen img': {
          height: '100%',
          minHeight: '100%',
        },
        '& $imageGrid img': {
          opacity: 1,
        },
        '& $pageTitle, & $pageBody': {
          maxWidth: '100%',
        },
      },
    },
    templateHeroFeature: {
      alignContent: 'center',
      color: '#fff',
      minHeight: 'var(--magazine-page-height, 100%)',
      '&::after': {
        background: 'rgba(27, 54, 76, .56)',
        content: '""',
        inset: 0,
        pointerEvents: 'none',
        position: 'absolute',
        zIndex: 1,
      },
      '& $pageTitle': {
        fontSize: '3.85rem',
        maxWidth: '78%',
      },
      '& $pageBody': {
        columns: 1,
        fontSize: '1rem',
        maxWidth: '68%',
      },
      '& $imageGrid': {
        inset: 0,
        position: 'absolute',
        zIndex: 0,
      },
      '& $imageGrid img': {
        opacity: 1,
      },
      '& > *:not($imageGrid)': {
        position: 'relative',
        zIndex: 3,
      },
      [theme.breakpoints.down('sm')]: {
        alignContent: 'start',
        minHeight: 'var(--magazine-page-height, calc(100dvh - 108px))',
        '& $pageTitle': {
          fontSize: '2.05rem',
          maxWidth: '100%',
        },
        '& $pageBody': {
          fontSize: '.95rem',
          maxWidth: '100%',
        },
        '& $imageGrid': {
          inset: 0,
          margin: 0,
          position: 'absolute',
          zIndex: 0,
        },
        '& $imageFullscreen': {
          inset: 0,
          minHeight: '100%',
          position: 'absolute',
        },
        '& $imageFullscreen img': {
          height: '100%',
          minHeight: '100%',
        },
        '& $imageGrid img': {
          opacity: 1,
        },
        '& $pageTitle, & $pageBody': {
          maxWidth: '100%',
        },
      },
    },
    templateProductEditorial: {
      gridTemplateRows: 'auto auto auto auto auto',
      '& $imageGrid': {
        gridTemplateColumns: '1fr',
        maxHeight: 'none',
      },
      '& $products': {
        alignSelf: 'start',
        maxWidth: 1180,
      },
    },
    templateProductImage: {
      alignContent: 'start',
      gridTemplateRows: 'auto auto 1fr auto',
      '& $pageBody': {
        columns: 1,
      },
      '& $imageGrid': {
        gridTemplateColumns: '1fr',
      },
      '& $imageWide:first-child': {
        aspectRatio: '16 / 9',
        minHeight: 420,
      },
      '& $products': {
        background: 'rgba(255, 253, 248, .94)',
        boxShadow: '0 14px 40px rgba(50, 38, 24, .12)',
        marginTop: theme.spacing(1.5),
        padding: theme.spacing(1.25),
      },
      [theme.breakpoints.down('sm')]: {
        '& $imageWide:first-child': {
          minHeight: 240,
        },
      },
    },
    templateCollage: {
      alignContent: 'start',
      '& $imageGrid': {
        alignItems: 'center',
        gridAutoRows: 'minmax(180px, auto)',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      },
      '& $imageGrid figure': {
        overflow: 'hidden',
      },
      '& $imageGrid figure:nth-child(even)': {
        transform: 'rotate(3deg) translate(var(--magazine-image-overlap-x, 0), var(--magazine-image-overlap-y, 0)) scale(var(--magazine-image-scale, 1))',
      },
      '& $imageGrid figure:nth-child(odd)': {
        transform: 'rotate(-2deg) translate(var(--magazine-image-overlap-x, 0), var(--magazine-image-overlap-y, 0)) scale(var(--magazine-image-scale, 1))',
      },
      [theme.breakpoints.down('sm')]: {
        '& $imageGrid': {
          gridTemplateColumns: '1fr',
        },
      },
    },
    templatePinboard: {
      alignContent: 'start',
      background: '#fffaf1',
      '& $pageBody': {
        columns: 1,
        maxWidth: 760,
      },
      '& $imageGrid': {
        alignItems: 'start',
        columnCount: 3,
        columnGap: theme.spacing(1.25),
        display: 'block',
      },
      '& $imageGrid figure': {
        background: '#fff',
        breakInside: 'avoid',
        boxShadow: '0 18px 42px rgba(65, 52, 38, .16)',
        margin: theme.spacing(0, 0, 1.25),
        minHeight: 0,
        padding: theme.spacing(.55),
      },
      '& $imageGrid figure:nth-child(3n + 1)': {
        transform: 'rotate(-1.6deg) translate(var(--magazine-image-overlap-x, 0), var(--magazine-image-overlap-y, 0)) scale(var(--magazine-image-scale, 1))',
      },
      '& $imageGrid figure:nth-child(3n + 2)': {
        transform: 'rotate(1.2deg) translate(var(--magazine-image-overlap-x, 0), var(--magazine-image-overlap-y, 0)) scale(var(--magazine-image-scale, 1))',
      },
      '& $imageGrid figure:nth-child(3n)': {
        transform: 'rotate(.4deg) translate(var(--magazine-image-overlap-x, 0), var(--magazine-image-overlap-y, 0)) scale(var(--magazine-image-scale, 1))',
      },
      '& $imageGrid img': {
        height: 'auto',
        minHeight: 0,
        objectFit: 'cover',
      },
      '& $products': {
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        marginTop: theme.spacing(2),
      },
      [theme.breakpoints.down('md')]: {
        '& $imageGrid': {
          columnCount: 2,
        },
      },
      [theme.breakpoints.down('sm')]: {
        '& $imageGrid': {
          columnCount: 1,
        },
      },
    },
    templateFaqText: {
      alignContent: 'start',
      gap: theme.spacing(2),
      minHeight: 'auto',
      padding: theme.spacing(4.5, 6),
      '& $pageKicker': {
        color: '#83745e',
      },
      '& $pageTitle': {
        fontSize: 'clamp(1.55rem, 2.9vw, 2.6rem)',
        lineHeight: 1.03,
        maxWidth: 820,
      },
      '& $pageSubtitle': {
        maxWidth: 780,
      },
      '& $pageBody': {
        background: 'rgba(143, 148, 123, .1)',
        borderLeft: '4px solid var(--magazine-accent)',
        columns: 1,
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '1rem',
        lineHeight: 1.65,
        maxWidth: 880,
        padding: theme.spacing(1.6, 2),
      },
      '& $imageGrid': {
        display: 'none',
      },
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2.5, 2),
        '& $pageBody': {
          padding: theme.spacing(1.25, 1.5),
        },
      },
    },
    productImageSlide: {
      background: 'transparent',
      boxShadow: 'none',
      boxSizing: 'border-box',
      display: 'grid',
      gap: theme.spacing(1.5),
      minHeight: 0,
      overflow: 'visible',
      padding: 0,
      width: '100%',
      [theme.breakpoints.down('sm')]: {
        minHeight: 'auto',
      },
    },
    productImageStage: {
      background: '#211f1c',
      boxShadow: '0 28px 70px rgba(0, 0, 0, .42)',
      margin: 0,
      minHeight: 520,
      overflow: 'hidden',
      position: 'relative',
      '& img': {
        display: 'block',
        height: 'auto',
        minHeight: 520,
        objectFit: 'cover',
        width: '100%',
      },
      '& figcaption': {
        background: 'rgba(255, 253, 248, .86)',
        bottom: 0,
        color: '#211f1c',
        fontSize: '.82rem',
        left: 0,
        padding: theme.spacing(.55, .75),
        position: 'absolute',
        right: 0,
      },
      [theme.breakpoints.down('sm')]: {
        minHeight: 260,
        '& img': {
          minHeight: 260,
        },
      },
    },
    productImageProducts: {
      display: 'grid',
      gap: theme.spacing(1),
      gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
      '& $productTile': {
        background: 'rgba(255, 253, 248, .98)',
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    },
    productImageEmpty: {
      alignItems: 'center',
      background: '#f1ece3',
      color: 'rgba(33, 31, 28, .58)',
      display: 'grid',
      minHeight: 420,
      placeItems: 'center',
    },
    productModalBackdrop: {
      alignItems: 'center',
      background: 'rgba(18, 17, 15, .58)',
      display: 'grid',
      inset: 0,
      padding: theme.spacing(2),
      placeItems: 'center',
      position: 'absolute',
      zIndex: 8,
    },
    productModal: {
      background: 'linear-gradient(135deg, #fffdf8 0%, #f6f0e7 100%)',
      boxShadow: '0 30px 90px rgba(0, 0, 0, .38)',
      color: '#211f1c',
      display: 'grid',
      gap: theme.spacing(0),
      gridTemplateColumns: 'minmax(220px, 42%) minmax(0, 1fr)',
      maxHeight: 'min(780px, 92vh)',
      maxWidth: 920,
      overflow: 'auto',
      position: 'relative',
      width: 'min(100%, 920px)',
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    },
    productModalImage: {
      alignItems: 'center',
      background: '#fff',
      display: 'grid',
      minHeight: 430,
      padding: theme.spacing(3),
      placeItems: 'center',
      position: 'relative',
      '& img': {
        display: 'block',
        maxHeight: 460,
        maxWidth: '100%',
        objectFit: 'contain',
        width: '100%',
      },
      '& span': {
        background: '#211f1c',
        color: '#fff',
        fontSize: '.72rem',
        fontWeight: 700,
        left: theme.spacing(2),
        letterSpacing: 0,
        padding: theme.spacing(.5, .8),
        position: 'absolute',
        textTransform: 'uppercase',
        top: theme.spacing(2),
      },
      [theme.breakpoints.down('sm')]: {
        minHeight: 260,
      },
    },
    productModalContent: {
      alignContent: 'center',
      display: 'grid',
      gap: theme.spacing(1.5),
      padding: theme.spacing(4),
      '& h3': {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '2rem',
        lineHeight: 1,
        margin: 0,
      },
      '& > span': {
        color: 'rgba(33, 31, 28, .58)',
        fontSize: '.78rem',
        fontWeight: 700,
        letterSpacing: 0,
        textTransform: 'uppercase',
      },
      '& p': {
        fontFamily: 'Georgia, "Times New Roman", serif',
        fontSize: '1rem',
        lineHeight: 1.45,
        margin: 0,
      },
      '& small': {
        color: 'rgba(33, 31, 28, .62)',
        lineHeight: 1.45,
      },
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2.25),
        '& h3': {
          fontSize: '1.55rem',
        },
      },
    },
    productModalActions: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: theme.spacing(1),
    },
    empty: {
      alignItems: 'center',
      color: 'rgba(33, 31, 28, .62)',
      display: 'grid',
      minHeight: 260,
      placeItems: 'center',
      textAlign: 'center',
    },
  })
)

export default useStyles
